/**
 * SoilGroupSetEditorPage — build or edit a reusable grouping template (U4).
 *
 * The same roster-table interaction as the U2 grouping builder, over the
 * tenant's whole orchard list instead of one session's. A set has no month and
 * no product, so it has no kg: there is no balance bar and no Auto-balance
 * here, because balancing nothing would be theatre.
 *
 * Every grouping decision reuses ./soilGrouping unchanged — its functions key on
 * orchard names and a group uid, neither of which is session-specific.
 *
 * This page is a SIBLING of the workspace layout (like the U2 grouping
 * builder and the U3 session detail screen), not one of its tabs, and its
 * `:id` is a *set* id — there is no programme to reconstruct from it. So every
 * link this page renders back into the workspace (the two crumbs, the
 * post-save navigate, and the SaveBar Cancel action) carries the incoming
 * `?program=` when the URL already has one; Task 5 carries the other half of
 * that round trip on the links INTO this page. Dropping either half silently
 * switches the user to a different programme (#429/#430's exact failure mode).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { DataTable, Field, FilterBar, Notice, SaveBar } from '../patterns';
import type { DataTableColumn, SaveBarStatus, SortState } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { soilProgramService } from '../../services/soilProgramService';
import { soilSessionService, SoilSessionFact, Operator } from '../../services/soilSessionService';
import { soilGroupSetService, SoilGroupSetGroup } from '../../services/soilGroupSetService';
import { formatMeasure } from '../../utils/formatMeasure';
import { generateUUID } from '../../utils';
import { naturalCompare } from '../../utils/naturalCompare';
import {
  DraftGroup, OrchardRow, buildRows, rangeSelect, assignToGroup, groupTotals, removeGroup, unassign,
  unassignedNames,
} from './soilGrouping';
import './nutrition.css';

const GROUP_COLOURS = ['#1E40AF', '#065F46', '#9D174D', '#5B21B6', '#92400E', '#155E75', '#B91C1C', '#3730A3'];
const groupColour = (index: number): string => GROUP_COLOURS[index % GROUP_COLOURS.length];

// A sentinel bulk-assign target meaning "remove from every group". Never a real
// group uid — uids are generateUUID() values — so this literal cannot collide
// with one. assignToGroup() already treats any unknown targetUid as "drop from
// current group, add to none" (mirrors SoilGroupingPage's identical sentinel).
const UNASSIGN_TARGET = '__unassign__';

// Cultivar chip ids are prefixed so a cultivar literally named "all" or
// "unassigned" cannot collide with the two reserved chip ids below.
const CULTIVAR_CHIP_PREFIX = 'cv:';

interface GsBlockers {
  noName: boolean;
  noGroups: boolean;
  emptyGroupLabels: string[];
}

// The backend (soilGroupSetService.js createGroupSet/updateGroupSet) enforces
// only a non-blank name — no minimum group count — but that permissiveness
// exists to avoid racing the unique-name index on create, not to bless an
// empty template. Applying a ZERO-GROUP set produces `groups: []` AND
// `skippedGroupLabels: []` (soilGroupSetApply.js:18-51 — the loop never runs),
// with every session orchard landing in `unassignedOrchardNames`. That preview
// reads "0 groups created · N orchards unassigned" — identical to what a
// perfectly valid set shows when it simply doesn't cover this session's
// orchards, giving the user no signal the set itself is empty. So, unlike the
// U2 grouping builder's findBlockers/canConfirm (which this screen
// deliberately does NOT reuse — there is still no per-group operator
// requirement here), `noGroups` IS enforced client-side even though the
// server would accept it.
const findGsBlockers = (name: string, groups: { label: string; orchardNames: string[] }[]): GsBlockers => ({
  noName: !name.trim(),
  noGroups: groups.length === 0,
  emptyGroupLabels: groups.filter((g) => g.orchardNames.length === 0).map((g) => g.label),
});

const canSaveGs = (b: GsBlockers): boolean => !b.noName && !b.noGroups && b.emptyGroupLabels.length === 0;

const toDraft = (g: SoilGroupSetGroup): DraftGroup => ({
  // A group SET's group has no persisted id (unlike a session's groupId) — it
  // is just a label/orchardNames/defaultOperators triple. buildRows/assignToGroup/
  // removeGroup all key on DraftGroup.uid, so one is minted client-side on load;
  // it never round-trips to the server (toPayload below drops it).
  uid: generateUUID(),
  label: g.label || '',
  orchardNames: g.orchardNames || [],
  operators: (g.defaultOperators || []).map((o) => ({ operatorId: o.operatorId, name: o.name })),
});

const SoilGroupSetEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // mongoose.Types.ObjectId.isValid('new') is false, so this sentinel can never
  // collide with a real id — the backend's isValid guard turns any accidental
  // request into a clean 404 anyway.
  const isNew = id === 'new';

  const programParam = searchParams.get('program');
  const withProgram = (p: string): string => (programParam ? `${p}${p.includes('?') ? '&' : '?'}program=${programParam}` : p);

  const [name, setName] = useState('');
  const [groups, setGroups] = useState<DraftGroup[]>([]);
  const [facts, setFacts] = useState<SoilSessionFact[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [target, setTarget] = useState<string>('');
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<'all' | 'unassigned' | string>('all');
  // Default: orchard name, natural order (so "B9" sorts before "B10").
  const [sort, setSort] = useState<SortState>({ key: 'orchardName', direction: 'asc' });
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Hooks must sit above every early return — a hook below a guard changes hook
  // order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.soil.workspace.title'), to: withProgram('/nutrition/soil') },
    { label: t('nutrition.soil.workspace.tab.setup'), to: withProgram('/nutrition/soil/setup') },
    { label: name || t('nutrition.soil.groupSets.crumbNew') },
  ]);
  // A user who has assigned dozens of orchards across several groups and then
  // hits F5 (or closes the tab) would otherwise lose everything silently.
  useBeforeUnloadWarning(dirty);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [f, ops, gs] = await Promise.all([
          soilProgramService.getOrchardFacts(),
          // Operators are a convenience for the picker, not core roster data —
          // a failed fetch here must not blank the whole screen.
          soilSessionService.listOperators().catch(() => []),
          // isNew skips the fetch entirely: soilGroupSetService.get is never
          // even called, not just ignored, so the 'new' sentinel can never
          // race a real request.
          isNew ? Promise.resolve(null) : soilGroupSetService.get(id as string),
        ]);
        if (cancelled) return;
        setFacts(f);
        setOperators(ops);
        if (gs) {
          setName(gs.name);
          setGroups(gs.groups.map(toDraft));
        }
        setError('');
      } catch (_e) {
        if (!cancelled) setError(t('nutrition.soil.groupSets.loadFailed'));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // `t` is deliberately absent: react-i18next's t is not referentially stable
    // and would re-fire this effect every render, clobbering in-progress edits.
    // `isNew` is derived from `id` every render, so `id` alone is the real key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const rows: OrchardRow[] = useMemo(
    () => buildRows(facts.map((f) => f.orchardName), facts, groups),
    [facts, groups],
  );

  const cultivars = useMemo(
    () => Array.from(new Set(rows.map((r) => r.cultivar).filter(Boolean))).sort(),
    [rows],
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !r.orchardName.toLowerCase().includes(q) && !r.cultivar.toLowerCase().includes(q)) return false;
      if (chip === 'unassigned' && r.groupUid !== null) return false;
      if (chip !== 'all' && chip !== 'unassigned' && r.cultivar !== chip.slice(CULTIVAR_CHIP_PREFIX.length)) return false;
      return true;
    });
  }, [rows, search, chip]);

  // rangeSelect operates on DISPLAYED order — post-filter AND post-sort (see
  // soilGrouping.ts) — so orderedNames must be derived from the SORTED rows,
  // never from `shown` directly.
  const sortedShown = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const arr = shown.slice();
    arr.sort((a, b) => {
      switch (sort.key) {
        case 'cultivar': return naturalCompare(a.cultivar, b.cultivar) * dir;
        case 'treeCount': return (a.treeCount - b.treeCount) * dir;
        case 'areaHa': return (a.areaHa - b.areaHa) * dir;
        default: return naturalCompare(a.orchardName, b.orchardName) * dir;
      }
    });
    return arr;
  }, [shown, sort]);

  const orderedNames = useMemo(() => sortedShown.map((r) => r.orchardName), [sortedShown]);

  const onToggle = useCallback((orchardName: string, shiftKey: boolean) => {
    setSelected((prev) => {
      if (shiftKey && anchor) return rangeSelect(prev, orderedNames, anchor, orchardName);
      return prev.includes(orchardName) ? prev.filter((n) => n !== orchardName) : [...prev, orchardName];
    });
    setAnchor(orchardName);
  }, [anchor, orderedNames]);

  // setGroups/setTarget are two separate top-level statements (not nested)
  // because React invokes state updaters twice under StrictMode — calling
  // setTarget from inside the setGroups updater would not be pure. A newly
  // added group becomes the bulk-assign target only when no target is set yet.
  const addGroup = () => {
    const uid = generateUUID();
    setGroups((prev) => [...prev, {
      uid, label: t('nutrition.soil.grouping2.groupN', { n: prev.length + 1 }), orchardNames: [], operators: [],
    }]);
    setTarget((cur) => cur || uid);
    setDirty(true);
  };

  const applyBulk = () => {
    if (!target || !selected.length) return;
    setGroups((prev) => assignToGroup(prev, selected, target));
    setSelected([]);
    setAnchor(null);
    setDirty(true);
  };

  const renameGroup = (uid: string, label: string) => {
    setGroups((prev) => prev.map((g) => (g.uid === uid ? { ...g, label } : g)));
    setDirty(true);
  };

  const dropGroup = (uid: string) => {
    // Only the group is removed; its orchards fall back to unassigned because
    // `rows` derives assignment from `groups`. They must never be lost.
    setGroups((prev) => removeGroup(prev, uid));
    setTarget((cur) => (cur === uid ? '' : cur));
    setDirty(true);
  };

  const unassignRow = (orchardName: string) => {
    setGroups((prev) => unassign(prev, orchardName));
    setDirty(true);
  };

  const addOperator = (uid: string, operatorId: string) => {
    if (!operatorId) return;
    const op = operators.find((o) => o.operatorId === operatorId);
    if (!op) return;
    setGroups((prev) => prev.map((g) => (
      g.uid !== uid || g.operators.length >= 4 || g.operators.some((o) => o.operatorId === operatorId)
        ? g
        : { ...g, operators: [...g.operators, { operatorId: op.operatorId, name: op.name }] }
    )));
    setDirty(true);
  };

  const removeOperator = (uid: string, operatorId: string) => {
    setGroups((prev) => prev.map((g) => (
      g.uid === uid ? { ...g, operators: g.operators.filter((o) => o.operatorId !== operatorId) } : g
    )));
    setDirty(true);
  };

  // A blank/whitespace-only group name must never reach the server or a
  // blocker/chip/select label. Not wrapped in useMemo/useCallback: it reads
  // `t`, and `t` must never sit in a memo/effect dependency array.
  const labelFor = (g: DraftGroup, i: number): string => (g.label.trim() ? g.label : t('nutrition.soil.grouping2.groupN', { n: i + 1 }));

  const blockers = findGsBlockers(name, groups.map((g, i) => ({ ...g, label: labelFor(g, i) })));
  const blocked = !canSaveGs(blockers);

  const ungrouped = useMemo(
    () => unassignedNames(facts.map((f) => f.orchardName), groups),
    [facts, groups],
  );

  const buildPayload = (): { name: string; groups: SoilGroupSetGroup[] } => ({
    name: name.trim(),
    groups: groups.map((g, i) => ({
      label: labelFor(g, i),
      orchardNames: g.orchardNames,
      defaultOperators: g.operators.map((o) => ({ operatorId: o.operatorId, name: o.name })),
    })),
  });

  const onSave = async () => {
    if (blocked) return;
    setSaving(true); setSaveError('');
    try {
      if (isNew) {
        await soilGroupSetService.create(buildPayload());
        setDirty(false);
        // A new editor creates then returns to the setup list; an existing
        // editor updates and stays (there is nothing further to navigate to).
        navigate(withProgram('/nutrition/soil/setup'), { replace: true });
      } else {
        await soilGroupSetService.update(id as string, buildPayload());
        setDirty(false);
      }
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { code?: string } } })?.response;
      if (resp?.status === 409 || resp?.data?.code === 'DUP_NAME') {
        setSaveError(t('nutrition.soil.groupSets.dupName'));
      } else {
        setSaveError(t('nutrition.soil.groupSets.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  // Error branch returns BEFORE the loading guard, so a failed load renders a
  // notice rather than sitting on "loading" forever.
  if (error) return <div className="pat-page"><Notice tone="danger" testId="gs-error">{error}</Notice></div>;
  if (!loaded) return <div className="pat-page">{t('common.loading')}</div>;

  const headerCheckedAll = shown.length > 0 && shown.every((r) => selected.includes(r.orchardName));

  const columns: DataTableColumn<OrchardRow>[] = [
    {
      key: 'check',
      label: (
        <input
          type="checkbox"
          data-testid="gs-check-all"
          checked={headerCheckedAll}
          onChange={() => setSelected(headerCheckedAll ? [] : orderedNames.slice())}
          aria-label={t('nutrition.soil.grouping2.selectAllShown', { count: shown.length })}
        />
      ),
      render: (r) => (
        <input
          type="checkbox"
          data-testid={`gs-check-${r.orchardName}`}
          checked={selected.includes(r.orchardName)}
          onChange={(e) => onToggle(r.orchardName, (e.nativeEvent as MouseEvent).shiftKey)}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('nutrition.soil.grouping2.selectOrchard', { name: r.orchardName })}
        />
      ),
    },
    { key: 'orchardName', label: t('nutrition.soil.groupSets.col.orchard'), sortable: true },
    { key: 'cultivar', label: t('nutrition.soil.groupSets.col.cultivar'), sortable: true },
    { key: 'treeCount', label: t('nutrition.soil.groupSets.col.trees'), align: 'right', sortable: true, render: (r) => Math.round(r.treeCount) },
    { key: 'areaHa', label: t('nutrition.soil.groupSets.col.ha'), align: 'right', sortable: true, render: (r) => formatMeasure(r.areaHa) },
    {
      key: 'group',
      label: t('nutrition.soil.groupSets.col.group'),
      render: (r) => {
        const idx = groups.findIndex((g) => g.uid === r.groupUid);
        return r.groupUid
          ? (
            <span className="ntr-grp-chip" style={{ background: `${groupColour(idx)}1A`, color: groupColour(idx) }}>
              {labelFor(groups[idx], idx)}
              <button
                type="button"
                onClick={() => unassignRow(r.orchardName)}
                aria-label={t('nutrition.soil.groupSets.unassignRow', { name: r.orchardName })}
              >×</button>
            </span>
          )
          : <span className="ntr-grp-chip ntr-grp-chip--none">{t('nutrition.soil.grouping2.unassignedCell')}</span>;
      },
    },
  ];

  return (
    <div className="pat-page" data-testid="soil-gs-editor-page">
      <PageHeader
        title={name || t('nutrition.soil.groupSets.newTitle')}
        meta={t('nutrition.soil.groupSets.meta', { orchards: rows.length, groups: groups.length })}
        actions={(
          <button type="button" className="pat-btn" data-testid="gs-add-group" onClick={addGroup}>
            {t('nutrition.soil.groupSets.addGroup')}
          </button>
        )}
      />

      <div className="ntr-gs-body">
        <Field label={t('nutrition.soil.groupSets.nameLabel')} controlId="gs-name">
          <input
            id="gs-name"
            type="text"
            className="pat-input"
            data-testid="gs-name"
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
          />
        </Field>

        {blocked && (
          <div data-testid="gs-blockers">
            <Notice tone="warn">
              <ul className="ntr-gs-blockers">
                {blockers.noName && <li>{t('nutrition.soil.groupSets.blk.noName')}</li>}
                {blockers.noGroups && <li>{t('nutrition.soil.groupSets.blk.noGroups')}</li>}
                {blockers.emptyGroupLabels.length > 0 && <li>{t('nutrition.soil.groupSets.blk.empty', { groups: blockers.emptyGroupLabels.join(', ') })}</li>}
              </ul>
            </Notice>
          </div>
        )}

        <div className="ntr-gs-strip">
          {groups.map((g, i) => {
            const totals = groupTotals(g, facts);
            return (
              <div key={g.uid} className="ntr-gs-card" data-testid={`gs-card-${i}`} style={{ borderLeftColor: groupColour(i) }}>
                <div className="ntr-gs-card__head">
                  <input
                    className="ntr-gs-card__name" data-testid={`gs-rename-${i}`}
                    value={g.label} onChange={(e) => renameGroup(g.uid, e.target.value)}
                    aria-label={t('nutrition.soil.grouping2.renameGroup')}
                  />
                  <button
                    type="button" className="pat-btn pat-btn--subtle ntr-gs-card__x"
                    data-testid={`gs-remove-${i}`} onClick={() => dropGroup(g.uid)}
                    aria-label={t('nutrition.soil.grouping2.removeGroup', { label: labelFor(g, i) })}
                  >×</button>
                </div>
                <div className="ntr-gs-card__totals">
                  {t('nutrition.soil.groupSets.cardTotals', {
                    trees: Math.round(totals.treeCount), ha: formatMeasure(totals.areaHa), orchards: totals.orchardCount,
                  })}
                </div>
                <div className="ntr-gs-card__ops">
                  {g.operators.map((o) => (
                    <span key={o.operatorId} className="ntr-gs-op" data-testid={`gs-op-${i}-${o.operatorId}`}>
                      {o.name}
                      <button type="button" onClick={() => removeOperator(g.uid, o.operatorId)} aria-label={t('nutrition.soil.grouping2.removeOperator', { name: o.name })}>×</button>
                    </span>
                  ))}
                  <select
                    className="ntr-resolve-select" data-testid={`gs-op-add-${i}`} value=""
                    disabled={g.operators.length >= 4}
                    onChange={(e) => addOperator(g.uid, e.target.value)}
                    aria-label={t('nutrition.soil.grouping2.addOperator')}
                  >
                    <option value="">{g.operators.length >= 4 ? t('nutrition.soil.grouping2.opsFull') : t('nutrition.soil.grouping2.addOperator')}</option>
                    {operators.filter((o) => !g.operators.some((x) => x.operatorId === o.operatorId)).map((o) => (
                      <option key={o.operatorId} value={o.operatorId}>{o.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="ntr-gs-bulk" data-testid="gs-bulk-bar">
            <span>{t('nutrition.soil.groupSets.selected', { count: selected.length })}</span>
            <label className="ntr-grp-bulk__assign">
              {t('nutrition.soil.grouping2.assignTo')}
              <select className="ntr-resolve-select" data-testid="gs-bulk-target" value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">{t('nutrition.soil.grouping2.chooseGroup')}</option>
                <option value={UNASSIGN_TARGET}>{t('nutrition.soil.grouping2.unassignOption')}</option>
                {groups.map((g, i) => <option key={g.uid} value={g.uid}>{labelFor(g, i)}</option>)}
              </select>
            </label>
            <button type="button" className="pat-btn" data-testid="gs-bulk-apply" disabled={!target} onClick={applyBulk}>
              {t('nutrition.soil.grouping2.apply')}
            </button>
            <button type="button" className="pat-btn pat-btn--subtle" onClick={() => { setSelected([]); setAnchor(null); }}>
              {t('nutrition.soil.grouping2.clear')}
            </button>
          </div>
        )}

        <FilterBar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={t('nutrition.soil.grouping2.searchPlaceholder')}
          chips={[
            { id: 'all', label: t('nutrition.soil.grouping2.chipAll', { count: rows.length }), active: chip === 'all' },
            { id: 'unassigned', label: t('nutrition.soil.grouping2.chipUnassigned', { count: rows.filter((r) => r.groupUid === null).length }), active: chip === 'unassigned' },
            ...cultivars.map((c) => ({ id: `${CULTIVAR_CHIP_PREFIX}${c}`, label: c, active: chip === `${CULTIVAR_CHIP_PREFIX}${c}` })),
          ]}
          onChipClick={(chipId) => setChip(chipId)}
        />

        <DataTable<OrchardRow>
          rows={sortedShown}
          rowKey={(r) => r.orchardName}
          rowClassName={(r) => (selected.includes(r.orchardName) ? 'ntr-grp-row--sel' : '')}
          emptyState={t('nutrition.soil.grouping2.noMatches')}
          sort={sort}
          onSortChange={setSort}
          columns={columns}
          mobileCardRow={(r) => (
            <div>
              {/* Distinct from the table cell text (adds cultivar inline) — jsdom
                  has no breakpoint, so both the row and this card render at once,
                  and an unscoped text query must stay unique. */}
              <strong>{r.orchardName} · {r.cultivar || '—'}</strong>
            </div>
          )}
        />

        {ungrouped.length > 0 && (
          <div className="ntr-grp-ungrouped" data-testid="gs-ungrouped">
            {t('nutrition.soil.grouping2.ungrouped', { count: ungrouped.length, names: ungrouped.join(', ') })}
          </div>
        )}

        <SaveBar
          status={((): SaveBarStatus => {
            if (saveError) return { kind: 'error', message: saveError };
            if (saving) return { kind: 'saving' };
            return dirty ? { kind: 'dirty' } : null;
          })()}
          secondaryActions={[{
            label: t('nutrition.soil.groupSets.cancel'),
            tone: 'subtle',
            onClick: () => navigate(withProgram('/nutrition/soil/setup')),
          }]}
          primaryAction={{ label: t('nutrition.soil.groupSets.save'), onClick: onSave, disabled: saving || blocked }}
        />
      </div>
    </div>
  );
};

export default SoilGroupSetEditorPage;
